import React, { useState, useEffect } from 'react';
import { Paper, Typography, Button, Switch, IconButton, Collapse, TextField, Grid, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Autocomplete, Select, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import SavedPopup from './SavedPopup';

const AutomationItem = ({ name, description, googleSheetsUrl, sheetName, outreachTemplateId, reminderTemplateId, secondReminderTemplateId, notInterestedReminderTemplateId, importTime, isActive, onDelete, onUpdate, id, gmailAccount }) => {
  const [expanded, setExpanded] = useState(false);
  const [enabled, setEnabled] = useState(isActive === 'true');
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isDescriptionEditing, setIsDescriptionEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const [editedDescription, setEditedDescription] = useState(description);
  const [editedGoogleSheetsUrl, setEditedGoogleSheetsUrl] = useState(googleSheetsUrl);
  const [editedSheetName, setEditedSheetName] = useState(sheetName);
  const [editedOutreachTemplateId, setEditedOutreachTemplateId] = useState(outreachTemplateId);
  const [editedReminderTemplateId, setEditedReminderTemplateId] = useState(reminderTemplateId);
  const [editedSecondReminderTemplateId, setEditedSecondReminderTemplateId] = useState(secondReminderTemplateId);
  const [editedNotInterestedReminderTemplateId, setEditedNotInterestedReminderTemplateId] = useState(notInterestedReminderTemplateId);
  const [editedImportTime, setEditedImportTime] = useState(importTime);
  const [templates, setTemplates] = useState([]);
  const [showSavedPopup, setShowSavedPopup] = useState(false);
  const [editedOutreachTemplate, setEditedOutreachTemplate] = useState(null);
  const [editedReminderTemplate, setEditedReminderTemplate] = useState(null);
  const [editedSecondReminderTemplate, setEditedSecondReminderTemplate] = useState(null);
  const [editedNotInterestedReminderTemplate, setEditedNotInterestedReminderTemplate] = useState(null);
  const [editedGmailAccount, setEditedGmailAccount] = useState(gmailAccount);
  const [accounts, setAccounts] = useState([]);

  const handleNameClick = () => {
    setIsNameEditing(true);
  };

  const handleNameChange = (e) => {
    setEditedName(e.target.value);
  };

  const handleNameBlur = () => {
    setIsNameEditing(false);
    onUpdate(id, { name: editedName });
  };

  const handleDescriptionClick = () => {
    setIsDescriptionEditing(true);
  };

  const handleDescriptionChange = (e) => {
    setEditedDescription(e.target.value);
  };

  const handleDescriptionBlur = () => {
    setIsDescriptionEditing(false);
    onUpdate(id, { description: editedDescription });
  };

  const handleGoogleSheetsUrlChange = (e) => {
    setEditedGoogleSheetsUrl(e.target.value);
  };

  const handleSheetNameChange = (e) => {
    setEditedSheetName(e.target.value);
  };

  const handleImportTimeChange = (e) => {
    setEditedImportTime(e.target.value);
  };

  const handleTemplateChange = (templateType) => (event, newValue) => {
    const templateName = newValue ? newValue.name : '';
    switch (templateType) {
      case 'outreach':
        setEditedOutreachTemplateId(templateName);
        break;
      case 'reminder':
        setEditedReminderTemplateId(templateName);
        break;
      case 'secondReminder':
        setEditedSecondReminderTemplateId(templateName);
        break;
      case 'notInterestedReminder':
        setEditedNotInterestedReminderTemplateId(templateName);
        break;
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/email-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email templates:', error);
    }
  };

  const handleToggleActive = async () => {
    const newIsActive = !enabled;
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/templates/${id}`, {
        isActive: newIsActive.toString()
      });
      if (response.data) {
        setEnabled(newIsActive);
        onUpdate(id, { isActive: newIsActive.toString() });
      }
    } catch (error) {
      console.error('Failed to update automation active status:', error);
    }
  };

  const handleSaveSettings = async () => {
    const updatedData = {
      name: editedName,
      description: editedDescription,
      googleSheetsUrl: editedGoogleSheetsUrl,
      sheetName: editedSheetName,
      outreachTemplateId: editedOutreachTemplateId,
      reminderTemplateId: editedReminderTemplateId,
      secondReminderTemplateId: editedSecondReminderTemplateId,
      notInterestedReminderTemplateId: editedNotInterestedReminderTemplateId,
      importTime: editedImportTime,
      gmailAccount: editedGmailAccount,
    };

    console.log('Attempting to save settings:', updatedData);
    console.log('editedOutreachTemplateId:', editedOutreachTemplateId);

    try {
      const result = await onUpdate(id, updatedData);
      console.log('Update result:', result);
      setShowSavedPopup(true);
    } catch (error) {
      console.error('Failed to save settings:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
      alert(`Failed to save settings: ${error.message}`);
    }
  };

  const handleCloseSavedPopup = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSavedPopup(false);
  };

  useEffect(() => {
    if (expanded) {
      fetchTemplates();
      fetchGmailAccounts();
      console.log('Fetching Gmail accounts...');
    }
  }, [expanded]);

  useEffect(() => {
    if (templates.length > 0) {
      setEditedOutreachTemplate(templates.find(t => t.id === editedOutreachTemplateId) || null);
      setEditedReminderTemplate(templates.find(t => t.id === editedReminderTemplateId) || null);
      setEditedSecondReminderTemplate(templates.find(t => t.id === editedSecondReminderTemplateId) || null);
      setEditedNotInterestedReminderTemplate(templates.find(t => t.id === editedNotInterestedReminderTemplateId) || null);
    }
  }, [templates, editedOutreachTemplateId, editedReminderTemplateId, editedSecondReminderTemplateId, editedNotInterestedReminderTemplateId]);

  const findTemplateByName = (name) => {
    return templates.find(template => template.name === name) || null;
  };

  const renderTemplateSelect = (label, value, templateType) => (
    <FormControl fullWidth>
      <Autocomplete
        value={findTemplateByName(value)}
        onChange={handleTemplateChange(templateType)}
        options={templates}
        getOptionLabel={(option) => option.name || ''}
        renderInput={(params) => <TextField {...params} label={label} />}
        isOptionEqualToValue={(option, value) => option.name === value}
      />
    </FormControl>
  );

  const fetchGmailAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/gmail-accounts`);
      console.log('Gmail accounts received:', response.data);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch Gmail accounts:', error);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} sm={8}>
          {isNameEditing ? (
            <TextField
              value={editedName}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              variant="standard"
              autoFocus
              fullWidth
              sx={{ mb: 1 }}
            />
          ) : (
            <Typography variant="h6" onClick={handleNameClick} sx={{ cursor: 'pointer' }}>
              {editedName}
            </Typography>
          )}
          {isDescriptionEditing ? (
            <TextField
              value={editedDescription}
              onChange={handleDescriptionChange}
              onBlur={handleDescriptionBlur}
              variant="standard"
              fullWidth
              multiline
              placeholder="Add a description"
            />
          ) : (
            <Typography
              variant="body2"
              color={editedDescription ? "text.secondary" : "text.disabled"}
              onClick={handleDescriptionClick}
              sx={{ cursor: 'pointer', fontStyle: editedDescription ? 'normal' : 'italic' }}
            >
              {editedDescription || "Click to add description"}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={4} container justifyContent="flex-end" spacing={1}>
          <Grid item>
            <Switch checked={enabled} onChange={handleToggleActive} />
          </Grid>
          <Grid item>
            <IconButton onClick={() => setExpanded(!expanded)}>
              <SettingsIcon />
            </IconButton>
          </Grid>
          <Grid item>
            <IconButton onClick={() => onDelete(id)} color="error">
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <Collapse in={expanded}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="gmail-account-label">Gmail Account</InputLabel>
              <Select
                labelId="gmail-account-label"
                value={editedGmailAccount || ''}
                label="Gmail Account"
                onChange={(e) => setEditedGmailAccount(e.target.value)}
              >
                {accounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>
                    {account.name} ({account.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Google Sheets URL"
              variant="outlined"
              value={editedGoogleSheetsUrl}
              onChange={handleGoogleSheetsUrlChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Sheet Name"
              variant="outlined"
              value={editedSheetName}
              onChange={handleSheetNameChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Import Time"
              variant="outlined"
              type="time"
              value={editedImportTime}
              onChange={handleImportTimeChange}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300, // 5 min
              }}
            />
          </Grid>
          {/* Outreach Template dropdown */}
          <Grid item xs={12}>
            {renderTemplateSelect("Outreach Template", editedOutreachTemplateId, 'outreach')}
          </Grid>

          {/* Reminder Template dropdown */}
          <Grid item xs={12}>
            {renderTemplateSelect("Reminder Template", editedReminderTemplateId, 'reminder')}
          </Grid>

          {/* 2nd Reminder Template dropdown */}
          <Grid item xs={12}>
            {renderTemplateSelect("2nd Reminder Template", editedSecondReminderTemplateId, 'secondReminder')}
          </Grid>

          {/* Not Interested Reminder Template dropdown */}
          <Grid item xs={12}>
            {renderTemplateSelect("Not Interested Reminder Template", editedNotInterestedReminderTemplateId, 'notInterestedReminder')}
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleSaveSettings}>Save Settings</Button>
          </Grid>
        </Grid>
      </Collapse>
      <SavedPopup open={showSavedPopup} onClose={handleCloseSavedPopup} />
    </Paper>
  );
};

const AutomationSettings = () => {
  const [automations, setAutomations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [newAutomationName, setNewAutomationName] = useState('');
  const [newAutomationGmailAccount, setNewAutomationGmailAccount] = useState('');
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    fetchAutomations();
    fetchGmailAccounts();
  }, []);

  const fetchAutomations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/templates`);
      setAutomations(response.data);
    } catch (error) {
      console.error('Failed to fetch automations:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/templates/${id}`);
      setAutomations(automations.filter(automation => automation._id !== id));
    } catch (error) {
      console.error('Failed to delete automation:', error);
    }
  };

  const handleAddNew = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewAutomationName('');
    setNewAutomationGmailAccount('');
  };

  const handleCreateAutomation = async () => {
    if (newAutomationName.trim() && newAutomationGmailAccount) {
      try {
        const newAutomation = {
          name: newAutomationName,
          description: "New automation description",
          googleSheetsUrl: "",
          sheetName: "",
          outreachTemplateId: "",
          reminderTemplateId: "",
          secondReminderTemplateId: "",
          notInterestedReminderTemplateId: "",
          importTime: "",
          isActive: true,
          gmailAccount: newAutomationGmailAccount
        };
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/templates`, newAutomation);
        setAutomations([...automations, response.data]);
        handleCloseDialog();
      } catch (error) {
        console.error('Failed to create automation:', error);
        alert('Failed to create automation. Please try again.');
      }
    } else {
      alert('Please provide both a name and select a Gmail account');
    }
  };

  const handleUpdateAutomation = async (id, updatedData) => {
    console.log('Updating automation:', id, updatedData);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/templates/${id}`, updatedData);
      console.log('Server response:', response.data);
      setAutomations(automations.map(automation =>
        automation._id === id ? { ...automation, ...response.data } : automation
      ));
      return response.data;
    } catch (error) {
      console.error('Failed to update automation:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
      throw error;
    }
  };

  const fetchGmailAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/gmail-accounts`);
      console.log('Gmail accounts received:', response.data);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch Gmail accounts:', error);
    }
  };

  return (
    <div style={{ maxWidth: '650px', margin: '0 auto' }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3 }}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
          <Grid item xs={12} sm>
            <Typography variant="h5">
              Email Outreach - Overview
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
              Add new
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {automations.map(automation => (
        <AutomationItem
          key={automation._id}
          id={automation._id}
          name={automation.name}
          description={automation.description}
          googleSheetsUrl={automation.googleSheetsUrl}
          sheetName={automation.sheetName}
          outreachTemplateId={automation.outreachTemplateId}
          reminderTemplateId={automation.reminderTemplateId}
          secondReminderTemplateId={automation.secondReminderTemplateId}
          notInterestedReminderTemplateId={automation.notInterestedReminderTemplateId}
          importTime={automation.importTime}
          isActive={automation.isActive}
          onDelete={handleDelete}
          onUpdate={handleUpdateAutomation}
          gmailAccount={automation.gmailAccount}
        />
      ))}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Automation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Automation Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newAutomationName}
            onChange={(e) => setNewAutomationName(e.target.value)}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="new-gmail-account-label">Gmail Account</InputLabel>
            <Select
              labelId="new-gmail-account-label"
              value={newAutomationGmailAccount}
              label="Gmail Account"
              onChange={(e) => setNewAutomationGmailAccount(e.target.value)}
            >
              {accounts.map((account) => (
                <MenuItem key={account._id} value={account._id}>
                  {account.name} ({account.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleCreateAutomation} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AutomationSettings;